/**
 * CHAT-02b / CHAT-17 — resolve the `chat_cta_enabled` flag for component
 * rendering, in an SSR-/hydration-safe way.
 *
 * Two independent inputs decide the result:
 *   1. The DB flag (`chat_cta_enabled`) — read async. This is the value the
 *      server renders from, so the first client render MUST reproduce it.
 *   2. The CHAT-16 host-gated preview override (localStorage + `?chatPreview`)
 *      — a CLIENT-only signal that does not exist during SSR.
 *
 * If the override could influence the FIRST render, the server HTML (which has
 * no override) and the first client render would diverge and trip React's
 * hydration error #418 (text mismatch) — e.g. the agent nav settling on
 * "Messages" on the client while the server emitted "Leads". So the override is
 * folded in ONLY after mount: the first render (and SSR) returns the DB-flag
 * value alone — `null` while its async read is in flight, which is exactly what
 * the server rendered — and the override can flip the result ON only on the
 * post-mount re-render. The DB flag is NOT hardcoded OFF, so a future real
 * DB-flag flip stays hydration-safe: the first render still derives from the
 * same DB source the server used.
 *
 * Returns `null` while the DB read is in flight, then the boolean.
 */

import { useEffect, useState } from 'react';
import { isChatCtaDbEnabled } from '../src/lib/platformSettings';
import { isChatPreviewActive } from '../src/lib/chatPreview';

export function useChatCtaEnabled(): boolean | null {
  // DB-flag value — the SSR-consistent source. `null` until the async read
  // resolves; both the server and the first client render see this.
  const [dbEnabled, setDbEnabled] = useState<boolean | null>(null);
  // CHAT-16 preview override — resolved post-mount only. `false` on the first
  // render and during SSR, so it can never perturb hydration.
  const [previewOverride, setPreviewOverride] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // Fold the host-gated preview override in now (post-mount), never on the
    // first render. It mirrors isChatCtaEnabled()'s short-circuit: when active
    // it forces the flag ON and the DB read is skipped.
    if (isChatPreviewActive()) {
      setPreviewOverride(true);
      return () => {
        cancelled = true;
      };
    }

    isChatCtaDbEnabled()
      .then((value) => {
        if (!cancelled) setDbEnabled(value);
      })
      .catch(() => {
        if (!cancelled) setDbEnabled(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // The override can only force the flag ON, and only after mount. Until then
  // (and during SSR) the result is purely the DB-flag value.
  if (previewOverride) return true;
  return dbEnabled;
}
